import React, { useState, useMemo } from 'react';
import { Card, Form, Button, Row, Col, Badge } from 'react-bootstrap';

/**
 * Day of week ID to name mapping (fixed, matches DB seed)
 */
const DAY_NAMES = {
  1: 'Monday', 2: 'Tuesday', 3: 'Wednesday', 4: 'Thursday',
  5: 'Friday', 6: 'Saturday', 7: 'Sunday'
};

/**
 * Extract day/slot context string from a standing order audit log
 */
function getStandingOrderContext(log, slotMap) {
  const val = parseJSON(log.new_value) || parseJSON(log.old_value);
  if (!val) return '';
  const day = DAY_NAMES[val.dayOfWeekId] || '';
  const slot = slotMap[val.timeSlotId] || '';
  if (day && slot) return ` for ${day} ${slot}`;
  if (day) return ` for ${day}`;
  return '';
}

/**
 * Translate a raw audit log entry into a human-readable description
 */
function describeLogEntry(log, slotMap) {
  const who = log.user_location || log.user_name || 'Unknown';
  const isSystem = log.user_name === 'SYSTEM' || log.user_name === 'system';
  const isPOS = log.user_name === 'POS_SYSTEM';
  const actor = isSystem ? '🤖 System' : isPOS ? '🖥️ POS' : who;

  // ─── Orders ───
  if (log.table_name === 'orders') {
    if (log.action === 'CREATE') {
      const details = parseJSON(log.new_value);
      const orderType = details?.orderType || details?.order_type || '';
      if (orderType === 'standing' || isSystem) {
        return { actor, icon: '🔄', text: `Auto-submitted standing order #${log.record_id}` };
      }
      if (orderType === 'modified') {
        return { actor, icon: '⚡', text: `Placed modified order #${log.record_id}` };
      }
      return { actor, icon: '📝', text: `Placed order #${log.record_id}` };
    }

    if (log.action === 'UPDATE') {
      if (log.field_name === 'status') {
        const statusLabels = {
          pending: 'Pending',
          submitted: 'Submitted',
          confirmed: 'Confirmed',
          completed: 'Completed',
          cancelled: 'Cancelled',
          replaced: 'Replaced'
        };
        const oldLabel = statusLabels[log.old_value] || log.old_value;
        const newLabel = statusLabels[log.new_value] || log.new_value;
        return { actor, icon: '🔄', text: `Order #${log.record_id}: ${oldLabel} → ${newLabel}` };
      }
      return { actor, icon: '✏️', text: `Updated order #${log.record_id}` };
    }

    if (log.action === 'DELETE') {
      return { actor, icon: '🗑️', text: `Deleted order #${log.record_id}` };
    }
  }

  // ─── Standing Orders ───
  if (log.table_name === 'standing_orders') {
    const changes = getStandingOrderChanges(log);
    const context = getStandingOrderContext(log, slotMap);

    if (log.action === 'CREATE') {
      return { actor, icon: '📋', text: `Set up standing order${context}`, changes };
    }

    if (log.action === 'UPDATE') {
      if (changes && changes.length > 0) {
        return { actor, icon: '✏️', text: `Updated standing order${context}`, changes };
      }
      return { actor, icon: '✏️', text: `Saved standing order${context} (no changes)`, changes: null, dimmed: true };
    }

    if (log.action === 'DELETE') {
      return { actor, icon: '🗑️', text: `Removed standing order${context}` };
    }
  }

  // ─── Menu Items ───
  if (log.table_name === 'menu_items') {
    if (log.action === 'CREATE') {
      const details = parseJSON(log.new_value);
      const name = details?.itemName || '';
      return { actor, icon: '🍕', text: `Added menu item${name ? ': ' + name : ''}` };
    }
    if (log.action === 'UPDATE') {
      return { actor, icon: '✏️', text: `Updated menu item #${log.record_id}` };
    }
    if (log.action === 'DELETE') {
      return { actor, icon: '🗑️', text: `Deleted menu item #${log.record_id}` };
    }
  }

  // ─── Auth ───
  if (log.table_name === 'auth') {
    if (log.action === 'LOGIN') {
      const details = parseJSON(log.new_value);
      const loc = details?.location || '';
      return { actor, icon: '🔑', text: `Logged in${loc ? ' (' + loc + ')' : ''}` };
    }
    if (log.action === 'LOGIN_FAILED') {
      const reason = typeof log.new_value === 'string' ? log.new_value : 
                     (parseJSON(log.new_value) || '');
      return { actor: log.user_name || 'Unknown', icon: '🚫', text: `Failed login attempt: ${reason}` };
    }
  }

  // ─── Order Skips ───
  if (log.table_name === 'order_skips') {
    const details = parseJSON(log.new_value) || parseJSON(log.old_value);
    const daySlot = details ? `${details.dayName || ''} ${details.slotName || ''}`.trim() : '';
    const dateStr = details?.skipDate ? ` (${details.skipDate})` : '';
    if (log.action === 'SKIP') {
      return { actor, icon: '⏭️', text: `Skipped order${daySlot ? ' for ' + daySlot : ''}${dateStr}` };
    }
    if (log.action === 'UNSKIP') {
      return { actor, icon: '↩️', text: `Removed skip${daySlot ? ' for ' + daySlot : ''}${dateStr}` };
    }
  }

  // ─── Fallback ───
  const actionVerbs = { CREATE: 'Created', UPDATE: 'Updated', DELETE: 'Deleted' };
  const verb = actionVerbs[log.action] || log.action;
  return { actor, icon: '📝', text: `${verb} ${log.table_name} #${log.record_id}` };
}

/**
 * Parse standing order changes into human-readable diffs
 */
function getStandingOrderChanges(log) {
  try {
    const oldVal = parseJSON(log.old_value);
    const newVal = parseJSON(log.new_value);
    if (!oldVal || !newVal) return null;

    const prevOrders = oldVal.previousOrders || [];
    const newOrders = newVal.newOrders || [];

    const prevMap = {};
    const nameMap = {};
    for (const item of prevOrders) {
      prevMap[item.menuItemId] = item.quantity;
      nameMap[item.menuItemId] = item.itemName;
    }
    for (const item of newOrders) {
      if (!nameMap[item.menuItemId]) nameMap[item.menuItemId] = item.itemName;
    }

    const newMap = {};
    for (const item of newOrders) {
      newMap[item.menuItemId] = item.quantity;
    }

    const changes = [];
    const allKeys = new Set([...Object.keys(prevMap), ...Object.keys(newMap)]);
    for (const key of allKeys) {
      const prev = prevMap[key] || 0;
      const next = newMap[key] || 0;
      const name = nameMap[key] || `Item ${key}`;
      if (prev !== next) {
        if (prev === 0) {
          changes.push({ type: 'add', text: `${name}: ${next}` });
        } else if (next === 0) {
          changes.push({ type: 'remove', text: `${name}: removed` });
        } else {
          changes.push({ type: 'change', text: `${name}: ${prev} → ${next}` });
        }
      }
    }
    return changes;
  } catch (e) {
    return null;
  }
}

function parseJSON(val) {
  if (!val) return null;
  if (typeof val === 'object') return val;
  try { return JSON.parse(val); } catch { return null; }
}

function getActionColor(action) {
  switch (action) {
    case 'CREATE': return 'success';
    case 'UPDATE': return 'warning';
    case 'DELETE': return 'danger';
    default: return 'secondary';
  }
}

function formatTime(timestamp) {
  const d = new Date(timestamp);
  const now = new Date();
  const isToday = d.toDateString() === now.toDateString();

  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  const isYesterday = d.toDateString() === yesterday.toDateString();

  const time = d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

  if (isToday) return `Today ${time}`;
  if (isYesterday) return `Yesterday ${time}`;
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' }) + ` ${time}`;
}

function formatRawValue(value) {
  if (!value) return '—';
  try {
    const parsed = typeof value === 'string' ? JSON.parse(value) : value;
    return JSON.stringify(parsed, null, 2);
  } catch {
    return String(value);
  }
}

function AuditLogsTab({
  auditLogs,
  auditFilters,
  users,
  timeSlots,
  onFilterChange,
  onLoadLogs
}) {
  const [expandedRow, setExpandedRow] = useState(null);

  // Build slot ID → name map
  const slotMap = useMemo(() => {
    const map = {};
    if (timeSlots) {
      for (const ts of timeSlots) {
        map[ts.id] = ts.slot_name;
      }
    }
    return map;
  }, [timeSlots]);

  // Filter out no-change standing order saves when "Hide no-ops" is checked
  const filteredLogs = useMemo(() => {
    if (!auditFilters.changesOnly) return auditLogs;
    return auditLogs.filter(log => {
      if (log.table_name !== 'standing_orders') return true;
      const desc = describeLogEntry(log, slotMap);
      return !desc.dimmed;
    });
  }, [auditLogs, auditFilters.changesOnly, slotMap]);

  return (
    <Card className="fade-in">
      <Card.Header>📋 Activity Log</Card.Header>
      <Card.Body>
        {/* Filters */}
        <Row className="mb-4">
          <Col md={3}>
            <Form.Group>
              <Form.Label>Show</Form.Label>
              <Form.Select
                value={auditFilters.tableName}
                onChange={(e) => onFilterChange('tableName', e.target.value)}
              >
                <option value="">All Activity</option>
                <option value="orders">Orders</option>
                <option value="standing_orders">Standing Orders</option>
                <option value="menu_items">Menu Changes</option>
                <option value="order_skips">Order Skips</option>
                <option value="auth">Logins</option>
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label>Location</Form.Label>
              <Form.Select
                value={auditFilters.userId}
                onChange={(e) => onFilterChange('userId', e.target.value)}
              >
                <option value="">All Locations</option>
                {users.filter(u => u.role === 'customer').map(user => (
                  <option key={user.id} value={user.id}>
                    {user.location_name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={2}>
            <Form.Group>
              <Form.Label>Entries</Form.Label>
              <Form.Select
                value={auditFilters.limit}
                onChange={(e) => onFilterChange('limit', e.target.value)}
              >
                <option value="25">25</option>
                <option value="50">50</option>
                <option value="100">100</option>
                <option value="250">250</option>
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={2}>
            <Form.Group>
              <Form.Label>&nbsp;</Form.Label>
              <Form.Check
                type="checkbox"
                id="changesOnly"
                label="Hide no-ops"
                checked={auditFilters.changesOnly || false}
                onChange={(e) => onFilterChange('changesOnly', e.target.checked)}
                className="mt-2"
              />
            </Form.Group>
          </Col>
          <Col md={2} className="d-flex align-items-end">
            <Button variant="primary" onClick={onLoadLogs}>
              🔍 Load
            </Button>
          </Col>
        </Row>

        {/* Activity Feed */}
        {auditLogs.length === 0 ? (
          <div className="text-center text-muted py-5">
            <h5>Click "Load" to view recent activity</h5>
          </div>
        ) : (
          <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
            {filteredLogs.map((log, idx) => {
              const desc = describeLogEntry(log, slotMap);
              const isExpanded = expandedRow === idx;

              return (
                <div
                  key={idx}
                  style={{
                    padding: '10px 14px',
                    borderBottom: '1px solid #eee',
                    opacity: desc.dimmed ? 0.5 : 1,
                    cursor: 'pointer',
                    backgroundColor: isExpanded ? '#f8f9fa' : 'transparent'
                  }}
                  onClick={() => setExpandedRow(isExpanded ? null : idx)}
                >
                  {/* Main row */}
                  <div className="d-flex align-items-start">
                    <span style={{ fontSize: '1.2rem', marginRight: '10px', minWidth: '24px' }}>
                      {desc.icon}
                    </span>
                    <div style={{ flex: 1 }}>
                      <div>
                        <strong>{desc.actor}</strong>
                        <span className="ms-1">{desc.text}</span>
                        <Badge
                          bg={getActionColor(log.action)}
                          className="ms-2"
                          style={{ fontSize: '0.65rem', verticalAlign: 'middle' }}
                        >
                          {log.action}
                        </Badge>
                      </div>

                      {/* Standing order change details */}
                      {desc.changes && desc.changes.length > 0 && (
                        <div className="mt-1" style={{ fontSize: '0.85rem' }}>
                          {desc.changes.map((c, i) => (
                            <span
                              key={i}
                              className={`me-3 ${
                                c.type === 'add' ? 'text-success' :
                                c.type === 'remove' ? 'text-danger' : 'text-warning'
                              }`}
                            >
                              {c.type === 'add' ? '+ ' : c.type === 'remove' ? '− ' : ''}{c.text}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    <small className="text-muted text-nowrap ms-3">
                      {formatTime(log.timestamp)}
                    </small>
                  </div>

                  {/* Expanded technical details */}
                  {isExpanded && (
                    <div
                      className="mt-2 ms-4 p-2"
                      style={{
                        fontSize: '0.78rem',
                        backgroundColor: '#f0f0f0',
                        borderRadius: '4px',
                        color: '#666'
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Row>
                        <Col xs={6} md={3}><strong>Table:</strong> {log.table_name}</Col>
                        <Col xs={6} md={2}><strong>Record:</strong> #{log.record_id}</Col>
                        <Col xs={6} md={2}><strong>User:</strong> {log.user_name}</Col>
                        <Col xs={6} md={2}><strong>Field:</strong> {log.field_name || '—'}</Col>
                        <Col xs={6} md={3}><strong>IP:</strong> {log.ip_address || '—'}</Col>
                      </Row>
                      {(log.old_value || log.new_value) && (
                        <Row className="mt-1">
                          <Col md={6}>
                            <strong>Old:</strong>
                            <pre style={{ fontSize: '0.75rem', maxHeight: '120px', overflow: 'auto', margin: '4px 0' }}>
                              {formatRawValue(log.old_value)}
                            </pre>
                          </Col>
                          <Col md={6}>
                            <strong>New:</strong>
                            <pre style={{ fontSize: '0.75rem', maxHeight: '120px', overflow: 'auto', margin: '4px 0' }}>
                              {formatRawValue(log.new_value)}
                            </pre>
                          </Col>
                        </Row>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <div className="mt-3 text-muted">
          <small>
            <strong>Showing:</strong> {filteredLogs.length} entries
            {auditFilters.changesOnly && auditLogs.length !== filteredLogs.length && (
              <span className="ms-2 text-info">
                ({auditLogs.length - filteredLogs.length} no-ops hidden)
              </span>
            )}
            <span className="ms-3">Click any row for details</span>
          </small>
        </div>
      </Card.Body>
    </Card>
  );
}

export default AuditLogsTab;
